import React from 'react';
import {
  Button, TextInput, EmptyState, Alert, Badge,
} from '@parsina/framework';
import { QuestionSetSkeleton } from '../Visit.skeleton.jsx';
import { MessageCircleQuestion, Sparkles, Check, X, Send } from 'lucide-react';
import { C, CT, soft } from '../../../constants/theme';
import { autoDir, useTranslation } from '../../../i18n/I18nProvider.jsx';
import { listSeparator } from '../../../i18n/format';

// §3.2.4 — پرسش‌ها, the «دستیار بالینی» pattern.
//
//   "Identify clinical information gaps."
//   "Provide automated prompts to clarify symptoms or findings."
//   "Support diagnostic and treatment decision pathways."
//
// ── The loop this pane closes ──────────────────────────────────────────────
// The assistant is given the diagnoses under consideration and returns, per
// diagnosis, the questions that would confirm or exclude it — each carrying the
// SENTENCE to record for a yes and for a no. Answering one appends that
// sentence to the visit's subjective, which is the same text the differential
// reads from. So:
//
//   subjective → questions → an answer → a richer subjective → a better
//   differential
//
// That is what makes this a workflow rather than a list of prompts. A question
// that only asks leaves the dentist retyping the finding themselves, and the
// finding is the part that matters.
//
// ── Nothing here loads by itself ───────────────────────────────────────────
// Both calls COST PARSINA CREDITS, so the tab opens with an explanation and a
// button. An automatic generate on tab-open would bill a credit every time a
// dentist glanced at this pane.

// «بله» and «خیر» are not a primary action and its escape hatch — they are two
// equal, opposite commitments, each writing a DIFFERENT sentence into the visit
// note. They were `subtle` (navy at 5%) beside `outline` (white): two pale pills
// that looked the same, on a card where picking the wrong one puts a wrong
// finding in a clinical record. Affirm and deny get the two opposite semantic
// tones, at equal weight so neither is nudged.
const ANSWER_TONE = {
  yes: { bg: soft(CT.good, 0.13), ink: CT.goodInk, line: soft(CT.good, 0.4) },
  no: { bg: soft(CT.danger, 0.13), ink: CT.dangerInk, line: soft(CT.danger, 0.4) },
};

function QuestionCard({ q, t, busyKey, disabled, onAnswer, answered }) {
  // Each answer, with the sentence it will write. Built as data so the two
  // branches cannot drift apart — the previous pair was two hand-copied blocks.
  const choices = [
    { key: 'yes', Icon: Check, label: t('visit.questions.yes'), statement: q.positiveStatement },
    { key: 'no', Icon: X, label: t('visit.questions.no'), statement: q.negativeStatement },
  ];

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '10px 12px', marginBottom: 8, background: CT.surface,
    }}>
      <div style={{ fontSize: 13, color: C.textPrimary, lineHeight: 1.9 }}>{q.question}</div>
      {q.hint && (
        <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 3 }}>{q.hint}</div>
      )}

      {answered ? (
        <div style={{
          marginTop: 9, borderRadius: 9, padding: '7px 10px',
          background: soft(CT.good, 0.13), color: CT.goodInk,
          fontSize: 12, lineHeight: 1.8,
          display: 'flex', alignItems: 'flex-start', gap: 6,
        }} dir="auto">
          <Check size={14} strokeWidth={3} aria-hidden style={{ flexShrink: 0, marginTop: 3 }} />
          <span>{t('visit.questions.recorded', { statement: answered })}</span>
        </div>
      ) : (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 8, marginTop: 9,
        }}>
          {/* The sentence is ON the button, not in a `title`. Pressing one of
              these appends that sentence to the record; a tooltip is invisible
              on a tablet, invisible to the keyboard, and it was the only place
              this said what the button was about to write. */}
          {choices.map(({ key, Icon, label, statement }) => {
            const tone = ANSWER_TONE[key];
            const busy = busyKey === `${q.id}:${key}`;
            return (
              <Button
                key={key}
                variant="outline"
                className="h-auto min-h-11 flex-col items-stretch justify-center gap-1 py-2 text-start hover:brightness-95"
                style={{ background: tone.bg, color: tone.ink, borderColor: tone.line }}
                // Busy is per-answer, not per-pane: a dentist who presses «بله»
                // must see WHICH answer is being written, not eight cards that
                // all went grey at once.
                disabled={disabled || !!busyKey}
                aria-busy={busy}
                onClick={() => onAnswer(q, key, statement)}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 800,
                }}>
                  <Icon size={14} strokeWidth={3} aria-hidden />
                  {busy ? t('common.actions.saving') : label}
                </span>
                {statement && (
                  <span
                    dir="auto"
                    style={{ fontSize: 11.5, fontWeight: 500, lineHeight: 1.7, opacity: 0.85 }}
                  >
                    {statement}
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function QuestionsPane({ assistant, diagnoses, note, editable }) {
  const { t, formatNumber } = useTranslation();
  const {
    questions, loadingQuestions, questionsError, generate,
    answers, asking, askError, ask,
  } = assistant;

  const [question, setQuestion] = React.useState('');
  // questionId → the statement that was recorded, so an answered card shows
  // what it wrote rather than resetting to two buttons.
  const [answered, setAnswered] = React.useState({});
  // `questionId:yes|no` — WHICH answer is mid-write, not merely that one is.
  const [answeringKey, setAnsweringKey] = React.useState(null);
  const [failure, setFailure] = React.useState(null);

  const recorded = diagnoses?.diagnoses || [];

  const onGenerate = () => generate({ diagnoses: recorded });

  /**
   * Record an answer INTO the subjective.
   *
   * The statement is appended to the note's free-text notes field rather than
   * replacing anything: the dentist's own words stay first, and the assistant's
   * findings accumulate under them in the order they were confirmed.
   */
  const onAnswer = async (q, choice, statement) => {
    if (!statement || answeringKey) return;
    setFailure(null);
    setAnsweringKey(`${q.id}:${choice}`);
    try {
      const existing = note?.note?.dental?.notes || '';
      const err = await note.save({
        subjective: { notes: existing ? `${existing}\n${statement}` : statement },
      });
      if (err) {
        setFailure(err);
        return;
      }
      setAnswered((prev) => ({ ...prev, [q.id]: statement }));
    } finally {
      setAnsweringKey(null);
    }
  };

  const onAsk = async () => {
    const text = question.trim();
    if (!text) return;
    const err = await ask(text);
    if (!err) setQuestion('');
  };

  return (
    <div data-testid="visit-questions" data-state={questions ? 'content' : 'empty'}>
      {failure && (
        <Alert variant="error" style={{ marginBottom: 12 }}>
          {t('visit.questions.recordFailed')}
        </Alert>
      )}

      {/* ── The gap list ─────────────────────────────────────────────────── */}
      <div style={{
        border: `1px solid ${C.border}`, borderRadius: 10,
        padding: 12, marginBottom: 14, background: CT.surface2,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
          fontSize: 12.5, fontWeight: 800, color: C.textPrimary,
        }}>
          <Sparkles size={14} aria-hidden />
          {t('visit.questions.title')}
        </div>
        <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.9, marginBottom: 9 }}>
          {recorded.length > 0
            ? t('visit.questions.forDiagnoses', {
              list: recorded.map((d) => d.display).join(listSeparator()),
            })
            : t('visit.questions.noDiagnosesYet')}
        </div>
        <Button className="min-h-11" onClick={onGenerate} disabled={loadingQuestions}>
          {loadingQuestions ? t('visit.questions.generating') : t('visit.questions.generate')}
        </Button>
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
          {t('visit.ai.costsCredits')}
        </div>
      </div>

      {/* Why the answers are dead on a closed visit. They were simply disabled,
          which reads as a bug rather than as a finished visit. */}
      {!editable && (
        <Alert variant="info" style={{ marginBottom: 12 }}>{t('visit.readOnly.note')}</Alert>
      )}

      {questionsError === 'credits' && (
        <Alert variant="info" title={t('errors.credits.title')} style={{ marginBottom: 12 }}>
          {t('errors.credits.body')}
        </Alert>
      )}
      {questionsError === 'unavailable' && (
        <Alert variant="warning" style={{ marginBottom: 12 }}>
          {t('visit.questions.unavailable')}
        </Alert>
      )}

      {/* The button above already says it is generating; what the region
          draws meanwhile is the SHAPE of what it will hold — a diagnosis title
          and its question cards — not a second spinner. */}
      {loadingQuestions && <QuestionSetSkeleton label={t('common.loading')} />}

      {questions && !loadingQuestions && !questionsError && (
        <div style={{ marginBottom: 16 }}>
          {questions.fallback && (
            // Surfaced, not hidden: generic questions should read as generic,
            // or a dentist will take a template for a considered opinion.
            <Alert variant="info" style={{ marginBottom: 10 }}>
              {t('visit.questions.fallbackNote')}
            </Alert>
          )}
          {questions.items.length === 0 ? (
            <EmptyState
              icon={<MessageCircleQuestion size={40} color={C.textMuted} />}
              title={t('visit.questions.empty.title')}
              description={t('visit.questions.empty.description')}
            />
          ) : (
            questions.items.map((set) => (
              <div key={`${set.diagnosisCode}-${set.diagnosisName}`} style={{ marginBottom: 14 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7,
                }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: C.textPrimary }}>
                    {set.diagnosisName}
                  </span>
                  <Badge variant="outline">
                    {t('visit.questions.count', { count: formatNumber(set.questions.length) })}
                  </Badge>
                </div>
                {set.questions.map((q) => (
                  <QuestionCard
                    key={q.id}
                    q={q}
                    t={t}
                    busyKey={answeringKey}
                    disabled={!editable}
                    answered={answered[q.id]}
                    onAnswer={onAnswer}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Free-form Q&A ───────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
        <div style={{
          fontSize: 11.5, fontWeight: 800, color: C.textSecondary, marginBottom: 7,
        }}>
          {t('visit.questions.askTitle')}
        </div>

        {answers.map((a, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{
              fontSize: 12.5, fontWeight: 700, color: C.textPrimary, marginBottom: 4,
            }}>
              {a.question}
            </div>
            <div style={{
              background: CT.surface2, borderRadius: 9, padding: '9px 11px',
              fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9,
            }}>
              {a.answer}
              {a.followUps?.length > 0 && (
                <div style={{ marginTop: 7, fontSize: 12 }}>
                  <strong style={{ color: C.textPrimary }}>
                    {t('visit.questions.followUps')}{t('common.punct.colon')}
                  </strong>
                  <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
                    {a.followUps.map((f, j) => <li key={j}>{f}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}

        {askError === 'credits' && (
          <Alert variant="info" title={t('errors.credits.title')} style={{ marginBottom: 10 }}>
            {t('errors.credits.body')}
          </Alert>
        )}
        {askError === 'unavailable' && (
          <Alert variant="warning" style={{ marginBottom: 10 }}>
            {t('visit.questions.unavailable')}
          </Alert>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextInput
              dir={autoDir(question)}
              value={question}
              aria-label={t('visit.questions.askPlaceholder')}
              placeholder={t('visit.questions.askPlaceholder')}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                // Enter submits — a single-line field where the only action is
                // "ask" should not require reaching for the mouse.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onAsk();
                }
              }}
            />
          </div>
          <Button className="min-h-11" onClick={onAsk} disabled={asking || !question.trim()}>
            <Send size={13} aria-hidden style={{ marginInlineEnd: 4 }} />
            {asking ? t('visit.questions.asking') : t('visit.questions.ask')}
          </Button>
        </div>
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
          {t('visit.ai.costsCredits')}
        </div>
      </div>
    </div>
  );
}
